const SIZES = { smaller: 0.55, same: 1, bigger: 1.7, much_bigger: 2.8 };
const SPEEDS = { slower: 0.6, same: 1, faster: 1.7, much_faster: 2.6 };
const REPS = { once: 1, twice: 2, several: 4, relentless: 8 };

const CURSOR_MARKUP = `
  <svg class="arrow-windows" viewBox="0 0 18 22" width="18" height="22" aria-hidden="true">
    <path d="M1 1 L1 17 L5 13 L8.2 20 L10.6 19 L7.4 12 L13 12 Z" fill="#fff" stroke="#111" stroke-width="1" stroke-linejoin="round"/>
  </svg>
  <svg class="arrow-mac" viewBox="0 0 18 22" width="18" height="22" aria-hidden="true">
    <path d="M1.5 1.2 L1.5 16.2 L5.2 12.6 L8.2 19.2 L10.4 18.2 L7.4 11.6 L12.6 11.6 Z" fill="#111" stroke="#fff" stroke-width="1.15" stroke-linejoin="round"/>
  </svg>
  <svg class="hand" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
    <path d="M8 11.2V6.1a1.25 1.25 0 0 1 2.5 0V10.6 M10.5 10V4.3a1.25 1.25 0 0 1 2.5 0V10.6 M13 10.4V5.3a1.25 1.25 0 0 1 2.5 0v6.6c0 3-1.7 5.4-5 5.4h-1.5C6.4 17.3 5 15.8 5 13.6V9.4a1.25 1.25 0 0 1 2.5 0v1.8" fill="#fff" stroke="#111" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>
  <svg class="ibeam" viewBox="0 0 16 24" width="16" height="24" aria-hidden="true">
    <path d="M3 2 H13 M8 2 V22 M3 22 H13" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="square"/>
    <path d="M3 2 H13 M8 2 V22 M3 22 H13" stroke="#111" stroke-width="1" fill="none"/>
  </svg>
  <div class="bloop-glyph"></div>
`;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function pointAlong(points, amount) {
  const scaled = amount * (points.length - 1);
  const index = Math.min(points.length - 2, Math.floor(scaled));
  const fraction = scaled - index;
  return {
    x: points[index].x + (points[index + 1].x - points[index].x) * fraction,
    y: points[index].y + (points[index + 1].y - points[index].y) * fraction,
  };
}

const CURSOR_PAD = 28;

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function screenInset(box) {
  const pad = Math.max(8, Math.min(CURSOR_PAD, box.width / 2 - 1, box.height / 2 - 1));
  return {
    pad,
    minX: pad,
    maxX: box.width - pad,
    minY: pad,
    maxY: box.height - pad,
  };
}

// The gesture keeps its shape, begins where the cursor already is, and is
// shifted or scaled so the whole path stays inside the screen.
export function placeGesture(points, originX, originY, box, options = {}) {
  const scale = options.scale || 1;
  const mirror = options.mirror ? -1 : 1;
  const inset = screenInset(box);
  let centerX = 0;
  let centerY = 0;
  points.forEach((point) => {
    centerX += point.x;
    centerY += point.y;
  });
  centerX /= points.length;
  centerY /= points.length;
  let relative = points.map((point) => ({
    x: (point.x - centerX) * scale * mirror,
    y: (point.y - centerY) * scale,
  }));
  let pathX = centerX;
  let pathY = centerY;
  if (!options.keepPlace) {
    const first = relative[0];
    relative = relative.map((point) => ({
      x: point.x - first.x,
      y: point.y - first.y,
    }));
    pathX = originX;
    pathY = originY;
  }

  const boundsOf = (path, ox, oy) => {
    let left = Infinity;
    let right = -Infinity;
    let top = Infinity;
    let bottom = -Infinity;
    path.forEach((point) => {
      const px = ox + point.x;
      const py = oy + point.y;
      left = Math.min(left, px);
      right = Math.max(right, px);
      top = Math.min(top, py);
      bottom = Math.max(bottom, py);
    });
    return { left, right, top, bottom };
  };

  let bounds = boundsOf(relative, pathX, pathY);
  const availW = Math.max(1, inset.maxX - inset.minX);
  const availH = Math.max(1, inset.maxY - inset.minY);
  const pathW = bounds.right - bounds.left;
  const pathH = bounds.bottom - bounds.top;
  if (pathW > availW || pathH > availH) {
    const fit = Math.min(availW / (pathW || 1), availH / (pathH || 1));
    relative = relative.map((point) => ({ x: point.x * fit, y: point.y * fit }));
    bounds = boundsOf(relative, pathX, pathY);
  }

  let shiftX = 0;
  let shiftY = 0;
  if (bounds.left < inset.minX - 0.5) shiftX = inset.minX - bounds.left;
  else if (bounds.right > inset.maxX + 0.5) shiftX = inset.maxX - bounds.right;
  if (bounds.top < inset.minY - 0.5) shiftY = inset.minY - bounds.top;
  else if (bounds.bottom > inset.maxY + 0.5) shiftY = inset.maxY - bounds.bottom;

  return {
    fromX: originX,
    fromY: originY,
    points: relative.map((point) => ({
      x: pathX + shiftX + point.x,
      y: pathY + shiftY + point.y,
    })),
  };
}

export function createBody(layer) {
  const cursor = document.createElement('div');
  cursor.className = 'bloop-cursor';
  cursor.dataset.sprite = 'arrow';
  cursor.dataset.platform = /Mac|iPhone|iPad/i.test(navigator.userAgent) ? 'mac' : 'windows';
  cursor.innerHTML = CURSOR_MARKUP;
  const glyph = cursor.querySelector('.bloop-glyph');
  const highlights = document.createElement('div');
  highlights.className = 'bloop-highlights';
  const tooltip = document.createElement('div');
  tooltip.className = 'bloop-tooltip';
  layer.append(highlights, tooltip, cursor);

  let x = -80;
  let y = -80;
  let tx = x;
  let ty = y;
  let visible = false;
  let mode = 'idle';
  let frozen = false;
  let raf = 0;
  let pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  let offset = { x: 180, y: -90 };
  let certainty = 0.4;
  let feeling = 'curious';
  let easeBoost = 0;
  let pending = null;
  let replay = null;
  let glyphTimer = null;
  let markTimer = null;
  let spriteTimer = 0;
  let allowOffscreen = false;
  let burst = false;
  let scrollX = window.scrollX || 0;
  let scrollY = window.scrollY || 0;
  let settleTimer = 0;
  let onIdle = null;

  function render() {
    cursor.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    cursor.style.opacity = visible ? '1' : '0';
  }

  function baseEase() {
    let ease = certainty > 0.8 ? 0.34 : certainty > 0.55 ? 0.18 : 0.09;
    if (feeling === 'excited' || feeling === 'startled') ease += 0.1;
    if (feeling === 'bored' || feeling === 'confused') ease = Math.min(ease, 0.06);
    return Math.min(0.5, ease + easeBoost);
  }

  function clampTarget(offscreen) {
    if (offscreen) return;
    const inset = screenInset(viewport());
    tx = clamp(tx, inset.minX, inset.maxX);
    ty = clamp(ty, inset.minY, inset.maxY);
  }

  function edgePoint(px, py) {
    const box = viewport();
    const options = [
      { x: -24, y: py, d: px },
      { x: box.width + 24, y: py, d: box.width - px },
      { x: px, y: -24, d: py },
      { x: px, y: box.height + 24, d: box.height - py },
    ];
    options.sort((a, b) => a.d - b.d);
    return options[0];
  }

  function clearMarks() {
    document.querySelectorAll('.bloop-hover, .bloop-press').forEach((node) => {
      node.classList.remove('bloop-hover', 'bloop-press');
    });
    highlights.replaceChildren();
    tooltip.classList.remove('is-on');
  }

  function showGlyph(symbol) {
    window.clearTimeout(glyphTimer);
    glyph.textContent = symbol || '';
    glyph.classList.toggle('is-on', Boolean(symbol));
    if (!symbol) return;
    glyphTimer = window.setTimeout(() => {
      glyph.classList.remove('is-on');
    }, 1200);
  }

  function showTooltip(node) {
    const text = node.getAttribute('data-bloop-tooltip');
    if (!text) {
      tooltip.classList.remove('is-on');
      return;
    }
    const rect = node.getBoundingClientRect();
    tooltip.textContent = text;
    tooltip.style.left = `${rect.left + rect.width / 2}px`;
    tooltip.style.top = `${rect.top}px`;
    tooltip.classList.add('is-on');
  }

  function highlight(node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    Array.from(range.getClientRects()).forEach((rect) => {
      if (rect.width < 1 || rect.height < 1) return;
      const box = document.createElement('div');
      box.className = 'bloop-highlight';
      box.style.left = `${rect.left}px`;
      box.style.top = `${rect.top}px`;
      box.style.width = `${rect.width}px`;
      box.style.height = `${rect.height}px`;
      highlights.appendChild(box);
    });
    range.detach();
  }

  function theatrical(node, type) {
    window.clearTimeout(markTimer);
    clearMarks();
    if (!node || !node.isConnected) return;
    if (type === 'select') {
      highlight(node);
    } else if (type === 'click' || type === 'copy') {
      node.classList.add('bloop-press');
      showTooltip(node);
    } else {
      node.classList.add('bloop-hover');
    }
    markTimer = window.setTimeout(clearMarks, type === 'select' ? 1400 : 900);
  }

  function updateSprite(now) {
    if (now - spriteTimer < 180) return;
    spriteTimer = now;
    const under = document.elementFromPoint(x + 2, y + 2);
    const style = under ? window.getComputedStyle(under).cursor : '';
    if (style.includes('pointer')) cursor.dataset.sprite = 'hand';
    else if (style.includes('text')) cursor.dataset.sprite = 'ibeam';
    else cursor.dataset.sprite = 'arrow';
  }

  function stepReplay(now) {
    if (now < replay.leadUntil) {
      const span = replay.leadUntil - replay.leadStarted;
      const t = span > 0 ? (now - replay.leadStarted) / span : 1;
      const eased = t * t * (3 - 2 * t);
      x = replay.fromX + (replay.points[0].x - replay.fromX) * eased;
      y = replay.fromY + (replay.points[0].y - replay.fromY) * eased;
      return;
    }
    const amount = (now - replay.started) / replay.duration;
    if (amount >= 1) {
      const end = replay.points[replay.points.length - 1];
      x = end.x;
      y = end.y;
      replay.index += 1;
      if (replay.index >= replay.reps) {
        tx = x;
        ty = y;
        becomeIdle();
        return;
      }
      beginLead(now);
      return;
    }
    const place = pointAlong(replay.points, Math.min(amount, 0.999));
    x = place.x;
    y = place.y;
  }

  function frame(now) {
    if (!visible && mode !== 'travel' && mode !== 'leave') {
      raf = 0;
      render();
      return;
    }
    raf = window.requestAnimationFrame(frame);
    if (frozen) {
      render();
      return;
    }
    if (mode === 'drift') {
      tx = pointer.x + offset.x;
      ty = pointer.y + offset.y;
      allowOffscreen = false;
      clampTarget(false);
    }
    if (mode === 'replay' && replay) {
      stepReplay(now);
    } else {
      const ease = baseEase();
      let dx = (tx - x) * ease;
      let dy = (ty - y) * ease;
      if (mode === 'travel' && !burst) {
        const step = Math.hypot(dx, dy);
        const maxStep = 16;
        if (step > maxStep) {
          dx = (dx / step) * maxStep;
          dy = (dy / step) * maxStep;
        }
      }
      x += dx;
      y += dy;
      if ((mode === 'travel' || mode === 'leave') && Math.hypot(tx - x, ty - y) < 8) {
        x = tx;
        y = ty;
        if (mode === 'leave') visible = false;
        else if (pending) {
          const action = pending;
          pending = null;
          theatrical(action.node, action.type);
        }
        becomeIdle();
      }
    }
    updateSprite(now);
    render();
  }

  function becomeIdle() {
    const finite = mode === 'replay' || mode === 'travel' || mode === 'leave';
    mode = 'idle';
    replay = null;
    easeBoost = 0;
    if (finite && onIdle) onIdle();
  }

  function ensureFrame() {
    if (!raf) raf = window.requestAnimationFrame(frame);
  }

  function shiftBy(dx, dy) {
    x -= dx;
    y -= dy;
    tx -= dx;
    ty -= dy;
    offset.x -= dx;
    offset.y -= dy;
    if (replay) {
      replay.fromX -= dx;
      replay.fromY -= dy;
      replay.points.forEach((point) => {
        point.x -= dx;
        point.y -= dy;
      });
    }
    highlights.querySelectorAll('.bloop-highlight').forEach((node) => {
      node.style.left = `${(parseFloat(node.style.left) || 0) - dx}px`;
      node.style.top = `${(parseFloat(node.style.top) || 0) - dy}px`;
    });
    if (tooltip.classList.contains('is-on')) {
      tooltip.style.left = `${(parseFloat(tooltip.style.left) || 0) - dx}px`;
      tooltip.style.top = `${(parseFloat(tooltip.style.top) || 0) - dy}px`;
    }
  }

  function recoverAfterScroll() {
    if (!visible || mode === 'leave' || allowOffscreen) return;
    const box = viewport();
    const safe = screenInset(box);
    const home = Math.max(safe.pad, Math.min(72, box.width / 2 - 1, box.height / 2 - 1));
    const clipped = x < safe.minX || x > safe.maxX || y < safe.minY || y > safe.maxY;
    if (!clipped) return;
    if (x < 0 || y < 0 || x > box.width || y > box.height) {
      x = clamp(x, -24, box.width + 24);
      y = clamp(y, -24, box.height + 24);
    }
    tx = clamp(x, home, box.width - home);
    ty = clamp(y, home, box.height - home);
    replay = null;
    pending = null;
    burst = false;
    easeBoost = 0;
    mode = 'travel';
    ensureFrame();
  }

  function onScroll() {
    const nextX = window.scrollX || 0;
    const nextY = window.scrollY || 0;
    const dx = nextX - scrollX;
    const dy = nextY - scrollY;
    scrollX = nextX;
    scrollY = nextY;
    if ((!dx && !dy) || !visible) return;
    shiftBy(dx, dy);
    render();
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(recoverAfterScroll, 320);
    ensureFrame();
  }

  function beginLead(now) {
    const gap = Math.hypot(replay.points[0].x - x, replay.points[0].y - y);
    const leadMs = gap > 1.5 ? Math.min(220, 40 + gap * 0.35) : 0;
    replay.fromX = x;
    replay.fromY = y;
    replay.leadStarted = now;
    replay.leadUntil = now + leadMs;
    replay.started = now + leadMs;
  }

  function startReplay(points, args, anticipate) {
    if (!points || points.length < 2) return false;
    const placed = placeGesture(points, x, y, viewport(), {
      scale: SIZES[args.size] || 1,
      mirror: Boolean(args.mirror),
      keepPlace: Boolean(args.keepPlace),
    });
    let length = 0;
    for (let index = 1; index < placed.points.length; index += 1) {
      length += Math.hypot(placed.points[index].x - placed.points[index - 1].x, placed.points[index].y - placed.points[index - 1].y);
    }
    const speed = (SPEEDS[args.speed] || 1) * (anticipate ? 1.45 : 1);
    const now = performance.now();
    replay = {
      points: placed.points,
      reps: REPS[args.repetitions] || 1,
      index: 0,
      duration: Math.max(260, (length / (420 * speed)) * 1000),
    };
    beginLead(now);
    mode = 'replay';
    pending = null;
    allowOffscreen = false;
    return true;
  }

  function describeNode(node, action) {
    const kind = node.getAttribute('data-bloop-kind') || 'item';
    const label = (node.getAttribute('data-bloop-label') || node.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
    const verb = action === 'click' || action === 'copy' ? 'clicked' : action === 'select' ? 'highlighted' : 'hovered';
    return `Bloop ${verb} the ${kind} '${label}' (${node.getAttribute('data-bloop-id')})`;
  }

  function goTo(id, context, fast) {
    if (!id) return null;
    const node = document.querySelector(`[data-bloop-id="${CSS.escape(id)}"]`);
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    const box = viewport();
    const inset = screenInset(box);
    if (rect.bottom < 0 || rect.top > box.height || rect.right < 0 || rect.left > box.width) return null;
    tx = clamp(rect.left + Math.min(18, rect.width / 2), inset.minX, inset.maxX);
    ty = clamp(rect.top + Math.min(14, rect.height / 2), inset.minY, inset.maxY);
    allowOffscreen = false;
    easeBoost = fast ? 0.16 : 0;
    mode = 'travel';
    replay = null;
    pending = { node, type: (context.visitorAction && context.visitorAction.type) || 'hover' };
    return describeNode(node, pending.type);
  }

  function flee(distanceName) {
    const box = viewport();
    burst = true;
    let dx = x - pointer.x;
    let dy = y - pointer.y;
    const length = Math.hypot(dx, dy) || 1;
    dx /= length;
    dy /= length;
    easeBoost = 0.18;
    replay = null;
    pending = null;
    if (distanceName === 'to_the_far_corner') {
      tx = dx >= 0 ? box.width - 28 : 28;
      ty = dy >= 0 ? box.height - 28 : 28;
      allowOffscreen = false;
      mode = 'travel';
      return;
    }
    const distance = distanceName === 'a_little' ? 140 : distanceName === 'off_the_edge' ? Math.max(box.width, box.height) : box.width * 0.5;
    tx = x + dx * distance;
    ty = y + dy * distance;
    allowOffscreen = distanceName === 'off_the_edge';
    if (!allowOffscreen) clampTarget(false);
    mode = 'travel';
  }

  function play(decision, context = {}) {
    const args = decision.args || {};
    const action = context.visitorAction || null;
    burst = false;
    certainty = typeof decision.certainty === 'number' ? decision.certainty : certainty;
    feeling = decision.feeling || feeling;
    showGlyph(decision.glyph);
    visible = true;
    ensureFrame();
    if (decision.verb === 'stay_still') {
      pending = null;
      becomeIdle();
      return null;
    }
    if (decision.verb === 'drift_along') {
      mode = 'drift';
      replay = null;
      return 'Bloop drifted along';
    }
    if (decision.verb === 'flee') {
      flee(args.flee_distance);
      return 'Bloop fled';
    }
    if (decision.verb === 'invite') {
      const wiggle = [];
      for (let index = 0; index <= 6; index += 1) {
        wiggle.push({ x: x + (index % 2 ? 14 : -14), y });
      }
      startReplay(wiggle, { size: 'same', speed: 'same', repetitions: 'twice', mirror: false }, false);
      return 'Bloop wiggled once';
    }
    if (decision.verb === 'lose_interest') {
      const box = viewport();
      tx = box.width - 70;
      ty = box.height - 70;
      easeBoost = 0;
      certainty = Math.min(certainty, 0.3);
      mode = 'travel';
      replay = null;
      pending = null;
      return 'Bloop drifted away';
    }
    const onElement = action && action.type !== 'move' && action.element_id;
    if (decision.verb === 'imitate_on_element' || (onElement && decision.verb !== 'imitate_gesture')) {
      const id = decision.verb === 'anticipate' ? (args.next_element || args.target) : args.target;
      return goTo(id, { visitorAction: action }, decision.verb === 'anticipate') || null;
    }
    if (decision.verb === 'anticipate' || decision.verb === 'imitate_gesture' || decision.verb === 'exaggerate') {
      if (context.path && startReplay(context.path, args, decision.verb === 'anticipate')) {
        if (decision.verb === 'exaggerate') return 'Bloop exaggerated the gesture';
        if (decision.verb === 'anticipate') return 'Bloop started the gesture early';
        return 'Bloop imitated the gesture';
      }
      return goTo(args.target || args.next_element, { visitorAction: action }, decision.verb === 'anticipate');
    }
    mode = 'idle';
    return null;
  }

  function enter(at) {
    if (at) pointer = { x: at.x, y: at.y };
    const box = viewport();
    const angle = Math.random() * Math.PI * 2;
    const distance = 150 + Math.random() * 100;
    const restingX = clamp(pointer.x + Math.cos(angle) * distance, 28, box.width - 28);
    const restingY = clamp(pointer.y + Math.sin(angle) * distance, 28, box.height - 28);
    offset = { x: restingX - pointer.x, y: restingY - pointer.y };
    const start = edgePoint(restingX, restingY);
    x = start.x;
    y = start.y;
    tx = restingX;
    ty = restingY;
    visible = true;
    mode = 'travel';
    pending = null;
    replay = null;
    certainty = 0.25;
    render();
    ensureFrame();
  }

  function showAt(px, py) {
    x = px;
    y = py;
    tx = px;
    ty = py;
    visible = true;
    mode = 'idle';
    offset = { x: px - pointer.x, y: py - pointer.y };
    render();
    ensureFrame();
  }

  function confetti(from) {
    const originX = visible ? x : from && from.x;
    const originY = visible ? y : from && from.y;
    if (originX == null || originY == null) return;
    const colors = ['#39ff14', '#00d9ff', '#ffb800', '#e6edf3'];
    const pieces = [];
    for (let index = 0; index < 34; index += 1) {
      const piece = document.createElement('span');
      piece.className = 'bloop-confetti';
      piece.style.background = colors[index % colors.length];
      const angle = Math.random() * Math.PI * 2;
      const speed = 90 + Math.random() * 240;
      pieces.push({
        el: piece,
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 120,
        rot: Math.random() * 360,
        spin: (Math.random() - 0.5) * 720,
      });
      layer.appendChild(piece);
    }
    const started = performance.now();
    let previous = started;
    function tick(now) {
      const dt = Math.min(0.032, (now - previous) / 1000);
      previous = now;
      const age = now - started;
      pieces.forEach((bit) => {
        bit.vy += 640 * dt;
        bit.x += bit.vx * dt;
        bit.y += bit.vy * dt;
        bit.rot += bit.spin * dt;
        bit.el.style.transform = `translate3d(${bit.x}px, ${bit.y}px, 0) rotate(${bit.rot}deg)`;
        bit.el.style.opacity = String(Math.max(0, 1 - age / 1000));
      });
      if (age < 1000) window.requestAnimationFrame(tick);
      else pieces.forEach((bit) => bit.el.remove());
    }
    window.requestAnimationFrame(tick);
  }

  ensureFrame();
  window.addEventListener('scroll', onScroll, { passive: true });

  return {
    play,
    enter,
    showAt,
    hold() {
      pending = null;
      becomeIdle();
    },
    leave() {
      const edge = edgePoint(x, y);
      tx = edge.x;
      ty = edge.y;
      mode = 'leave';
      replay = null;
      pending = null;
      clearMarks();
      ensureFrame();
    },
    hide() {
      visible = false;
      render();
      clearMarks();
    },
    show() {
      visible = true;
      render();
    },
    freeze(next) { frozen = next; },
    setPointer(next) { pointer = { x: next.x, y: next.y }; },
    position() { return { x, y, visible }; },
    isMoving() {
      return mode === 'replay' || mode === 'travel';
    },
    setOnIdle(listener) { onIdle = listener; },
    confetti,
    pause() {
      window.cancelAnimationFrame(raf);
      raf = 0;
    },
    resume() { ensureFrame(); },
    destroy() {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(glyphTimer);
      window.clearTimeout(markTimer);
      window.clearTimeout(settleTimer);
      clearMarks();
      cursor.remove();
      highlights.remove();
      tooltip.remove();
    },
  };
}
